<!--
  ViewSwitcher.vue - Component for users to switch between React and Vue views

  2026-04-28: Made available to all user roles except investors
  2026-02-17: Made available to all admins, added Beta tag + tooltip, same-route navigation
  2026-02-15: Added move-all-modules API call to set all modules to target version on switch
  2025-08-24: Added environment-aware routing (localhost for dev, relative paths for prod)
  2025-08-24: Updated to compact single-button design for side-by-side layout
  2025-08-24: Moved to sidebar menu next to product type picker for better organization
  2025-08-24: Created view switcher for super admin navigation between React/Vue apps
-->
<template>
  <div v-if="!isInvestor">
    <div class="d-flex align-center" style="gap: 8px">
      <v-btn
        :disabled="isTransitioning"
        :loading="isTransitioning"
        small
        outlined
        color="grey darken-1"
        class="compact-view-switcher flex-grow-1"
        :title="`Switch to ${
          targetView === 'vue' ? 'V1 (Vue)' : 'V2 (React)'
        } view`"
        @click="handleViewSwitch"
      >
        <div class="status-dot"></div>
        <span class="mx-1">{{ currentView === 'vue' ? 'V1' : 'V2' }}</span>
        <v-icon small>mdi-swap-horizontal</v-icon>
        <span class="mx-1">{{
          isTransitioning ? '...' : targetView === 'vue' ? 'V1' : 'V2'
        }}</span>
      </v-btn>
      <v-chip x-small color="teal" text-color="white" class="beta-chip">
        Beta
      </v-chip>
      <v-tooltip bottom max-width="220">
        <template #activator="{ on, attrs }">
          <v-icon small color="grey" v-bind="attrs" v-on="on">
            mdi-information-outline
          </v-icon>
        </template>
        <span>
          This is a beta version of the new interface. You can switch back to V1
          anytime using this toggle.
        </span>
      </v-tooltip>
    </div>
  </div>
</template>

<script>
import axios from '@/axios/config'
import CommonFunctions from 'components/mixins/common_functions'
import PermissionsMixin from 'components/mixins/permissions-mixin'

/**
 * Route mappings from Vue (V1) paths to React (V2) paths
 * Vue uses path params for tabs (e.g. /guest-experience/all)
 * React uses query params (e.g. /crm?tab=all) or path segments
 *
 * Each entry can optionally define tabParam handling:
 *   tabParam: 'query' - Vue path tab → React query param ?tab=
 *   tabParam: 'path'  - Vue path tab → appended to React path
 *   tabParam: 'map'   - Vue path tab → custom mapped React path
 */
const V1_TO_V2_ROUTES = [
  { v1: '/dashboard/system', v2: '/app/dashboard/system' },
  { v1: '/dashboard/listings', v2: '/app/listings' },
  { v1: '/dashboard/multi-calendar', v2: '/app/calendar' },
  {
    v1: '/dashboard/guest-experience',
    v2: '/app/crm',
    tabParam: 'query',
  },
  {
    v1: '/dashboard/reservation',
    v2: '/app/reservation',
    tabParam: 'path',
  },
  {
    v1: '/dashboard/service-calls',
    v2: '/app/dashboard/service-calls',
  },
  { v1: '/dashboard/taskim', v2: '/app/taskim/query' },
  { v1: '/dashboard/iot', v2: '/app/iot-devices' },
  { v1: '/dashboard/auto-tasks', v2: '/app/auto-task-rules' },
  {
    v1: '/dashboard/guest-mgmt',
    v2: '/app/experience',
    tabParam: 'map',
    tabMap: {
      'pre-check-ins': '/app/experience/pre-check-ins',
      store: '/app/experience/purchases',
      requests: '/app/experience/requests',
      settings: '/app/experience/settings',
    },
    defaultV2: '/app/experience/pre-check-ins',
  },
  {
    v1: '/dashboard/auto-messages',
    v2: '/app/guests/automated-messages',
  },
  { v1: '/dashboard/cm', v2: '/app/channels' },
  {
    v1: '/dashboard/reviews-dashboard',
    v2: '/app/reviews',
    tabParam: 'query',
  },
  { v1: '/dashboard/pricing-panel', v2: '/app/pricings' },
  {
    v1: '/dashboard/bank-feeds',
    v2: '/app/finance/bank-transactions',
  },
  { v1: '/dashboard/ai-config', v2: '/app/ai-config' },
  { v1: '/dashboard/management', v2: '/app/integrations-v2' },
  { v1: '/dashboard/feeds', v2: '/app/feeds' },
  { v1: '/dashboard/guest-book', v2: '/app/guest-book' },
  { v1: '/dashboard/invoices', v2: '/app/dashboard/invoices' },
  {
    v1: '/dashboard/portfolio',
    v2: '/app/dashboard/portfolio/overview',
  },
  {
    v1: '/dashboard/operations-settings',
    v2: '/app/dashboard/operations-settings',
  },
]

export default {
  name: 'ViewSwitcher',
  mixins: [CommonFunctions, PermissionsMixin],
  data() {
    return {
      isTransitioning: false,
    }
  },
  computed: {
    currentView() {
      const port = window.location.port
      if (port === '8081') return 'react'
      if (port === '8080') return 'vue'
      return 'vue' // default for Vue app
    },
    targetView() {
      return this.currentView === 'react' ? 'vue' : 'react'
    },
  },
  methods: {
    /**
     * Maps the current Vue route to its equivalent React (V2) route
     * Handles Vue path-param tabs → React query-param or path conversions
     *
     * @return {string} The mapped V2 route path with query params
     */
    getV2RouteForCurrentPath() {
      const currentPath = this.$route?.path || '/dashboard/system'

      for (const route of V1_TO_V2_ROUTES) {
        // Exact match
        if (currentPath === route.v1) {
          return route.v2
        }

        // Prefix match (has additional path segments like tab params)
        if (currentPath.startsWith(route.v1 + '/')) {
          const suffix = currentPath.slice(route.v1.length + 1)
          const parts = suffix.split('/')
          const tab = parts[0]

          if (route.tabParam === 'query' && tab) {
            return `${route.v2}?tab=${tab}`
          }

          if (route.tabParam === 'path') {
            return `${route.v2}/${suffix}`
          }

          if (route.tabParam === 'map' && route.tabMap) {
            return route.tabMap[tab] || route.defaultV2 || route.v2
          }

          // No tab handling - append suffix as path
          return `${route.v2}/${suffix}`
        }
      }

      // Default fallback
      return '/app/dashboard/system'
    },

    async handleViewSwitch() {
      if (this.isTransitioning) return

      this.isTransitioning = true

      // Move all modules to the target version (V1=1 for Vue, V2=2 for React)
      const targetVersion = this.targetView === 'react' ? 2 : 1
      try {
        await axios.post('/api/users/move-all-modules', {
          target_version: targetVersion,
        })
      } catch (err) {
        console.error('Error moving all modules:', err)
      }

      // Determine target URL based on current view and environment
      let targetUrl
      const isDevelopment =
        process.env.NODE_ENV === 'development' ||
        window.location.hostname === 'localhost'

      if (this.currentView === 'react') {
        // Switch to Vue - use current path as-is
        const currentPath = window.location.pathname
        if (isDevelopment) {
          targetUrl = `http://localhost:8080${currentPath}`
        } else {
          targetUrl = currentPath
        }
      } else {
        // Switch to React - map current Vue route to V2 equivalent
        const v2Route = this.getV2RouteForCurrentPath()
        if (isDevelopment) {
          targetUrl = `http://localhost:8081${v2Route}`
        } else {
          targetUrl = v2Route
        }
      }

      // Navigate to the target URL
      window.location.href = targetUrl
    },
  },
}
</script>

<style scoped>
.compact-view-switcher {
  font-size: 12px !important;
  min-width: auto !important;
  height: 32px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.status-dot {
  width: 8px;
  height: 8px;
  background-color: #4caf50;
  border-radius: 50%;
  margin-right: 4px;
}

.beta-chip {
  height: 20px !important;
  font-size: 10px !important;
}
</style>
